import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { memo, useEffect, useMemo, useState } from "react";
import { TransactionListItem } from "@common/schemas/transaction.schema";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { MatchedScheduleCell } from "@components/accounting/schedules/MatchedScheduleCell";
import { SchedulePicker } from "@components/accounting/schedules/SchedulePicker";
import { resolvePickerTemplates } from "@components/accounting/schedules/schedulePickerTemplates";
import { useAccrualAccountType } from "@components/accounting/schedules/useAccrualAccountType";
import { filterTemplatesForTransaction } from "@components/accounting/schedules/transactionScheduleTemplates";
import { getTransactionsTableMeta } from "@hooks/view/useViewTableMeta";
import { useIsReadonlyOrLocked } from "@hooks/useIsReadonlyOrLocked";
import { useTypedFlags } from "@hooks/useTypedFlags";
import { useTransactionSchedulePicker } from "./useTransactionSchedulePicker";

export const ScheduleCell = memo(
    ({
        row,
        table,
        column,
    }: CellContext<
        ViewTableFeatures,
        TransactionListItem,
        TransactionListItem["schedule"]
    >) => {
        const isReadonlyOrLocked = useIsReadonlyOrLocked(row.original, table);
        const [isOpen, setIsOpen] = useState(false);
        const meta = getTransactionsTableMeta(table);
        const { schedulesExpenses: isExpensesEnabled } = useTypedFlags();
        const accountType = useAccrualAccountType(
            row.original.accrualAccountId,
        );

        const scheduleTemplates = useMemo(
            () =>
                filterTemplatesForTransaction({
                    templates: resolvePickerTemplates({
                        templates: meta.scheduleTemplates,
                        entityId: row.original.entity.id,
                        prioritizedTemplateId:
                            meta.prioritizedScheduleTemplateId,
                        prioritizedTemplate: meta.prioritizedScheduleTemplate,
                    }),
                    amount: row.original.amount,
                    accountType,
                    isExpensesEnabled,
                }),
            [
                accountType,
                isExpensesEnabled,
                meta.prioritizedScheduleTemplateId,
                meta.prioritizedScheduleTemplate,
                meta.scheduleTemplates,
                row.original.amount,
                row.original.entity.id,
            ],
        );

        const registerCellActionOpener = meta.registerCellActionOpener;
        // Not a render-sync effect: subscribes this cell's picker to the
        // table's imperative cell-action registry and unregisters on cleanup.
        useEffect(
            () =>
                registerCellActionOpener?.({
                    columnId: column.id,
                    rowId: row.original.id,
                    open: () => setIsOpen(true),
                }),
            [column.id, registerCellActionOpener, row.original.id],
        );

        const { matchedSchedules, pickerProps, overlays } =
            useTransactionSchedulePicker({
                transaction: row.original,
                isReadonlyOrLocked,
                scheduleCallbacks: meta.callbacks,
            });

        if (matchedSchedules.length > 0) {
            return (
                <ViewTableCell captureClicks>
                    <MatchedScheduleCell
                        schedules={matchedSchedules}
                        counterpart="invoice"
                    />
                </ViewTableCell>
            );
        }

        return (
            <ViewTableCell captureClicks>
                <SchedulePicker
                    templates={scheduleTemplates}
                    isOpen={isOpen}
                    onOpenChange={setIsOpen}
                    prioritizedTemplateId={meta.prioritizedScheduleTemplateId}
                    {...pickerProps}
                />
                {overlays}
            </ViewTableCell>
        );
    },
);

ScheduleCell.displayName = "ScheduleCell";
